import { useCallback } from 'react';

/**
 * Custom hook for capturing DOM state for agent context
 * Provides utilities to scrape current table view and active filters
 */
/**
 * Helper function to capture pagination context
 * @returns {Object|null} Pagination information
 */
const capturePaginationContext = () => {
  console.log('📄 [DOM Scraping] Looking for pagination context...');
  
  // Look for "Page X of Y" pattern anywhere in the DOM (our PaginationControls component)
  const pagePattern = /Page\s+(\d+)\s+of\s+(\d+)/i;
  
  // Search through buttons and divs that might contain pagination text
  const paginationSelectors = [
    'button',
    'div',
    'span',
    '[class*="pagination"]',
    '[title*="page"]'
  ];
  
  for (const selector of paginationSelectors) {
    const elements = document.querySelectorAll(selector);
    for (const element of elements) {
      const text = element.textContent || '';
      const match = text.match(pagePattern);
      
      if (match) {
        const currentPage = parseInt(match[1]);
        const totalPages = parseInt(match[2]);
        
        console.log(`📄 [DOM Scraping] ✅ Found pagination: ${match[0]}`);
        
        return {
          currentPage,
          totalPages,
          hasMultiplePages: totalPages > 1,
          paginationText: match[0],
          capturedFrom: element.tagName.toLowerCase()
        };
      }
    }
  }
  
  console.log('📄 [DOM Scraping] ❌ No pagination context found');
  return null;
};

export const useDOMCapture = () => {
  
  /**
   * Captures the current analysis table view including headers and visible rows
   * @returns {Object|null} Table data with headers, rows, and metadata
   */
  const captureCurrentTableView = useCallback(() => {
    console.log('🔍 [DOM Scraping] Looking for analysis-results-table...');
    
    const table = document.getElementById('analysis-results-table');
    if (!table) {
      console.log('🔍 [DOM Scraping] ❌ Analysis table not found');
      return null;
    }
    
    console.log('🔍 [DOM Scraping] ✅ Found analysis table');
    
    // Capture headers
    const headerRow = table.querySelector('thead tr');
    let headers = '';
    if (headerRow) {
      const headerCells = Array.from(headerRow.querySelectorAll('th'));
      headers = headerCells
        .map(th => th.innerText.trim())
        .filter(text => text && text !== '')
        .join(' | ');
      console.log('🔍 [DOM Scraping] Headers:', headers);
    }
    
    // Capture visible rows (first 20, but filter out mobile duplicates)
    const tbody = table.querySelector('tbody');
    if (!tbody) {
      console.log('🔍 [DOM Scraping] ❌ No tbody found');
      return null;
    }
    
    const allRows = Array.from(tbody.querySelectorAll('tr'));
    
    // Filter out mobile card rows (they have class 'block sm:hidden' or similar)
    const desktopRows = allRows.filter(row => {
      const classes = row.className || '';
      // Skip mobile-only rows (block sm:hidden) and keep desktop rows (hidden sm:table-row)
      return !classes.includes('block') || classes.includes('sm:table-row');
    });
    
    const visibleRows = desktopRows.slice(0, 30); // Increased from 20 to 30 for better agent context
    console.log(`🔍 [DOM Scraping] Found ${allRows.length} total rows, ${desktopRows.length} desktop rows, taking first ${visibleRows.length}`);
    
    const rowsData = visibleRows.map((row, index) => {
      const cells = Array.from(row.querySelectorAll('td'));
      const rowText = cells
        .map(cell => {
          // Clean up cell content by handling multi-line values
          let text = cell.innerText.trim();
          
          // Replace line breaks with spaces to keep data on same row
          text = text.replace(/\n+/g, ' ');
          
          // Clean up extra whitespace
          text = text.replace(/\s+/g, ' ');
          
          return text;
        })
        .filter(text => text && text !== '')
        .join(' | ');
      
      // Capture row metadata for Magic Mode parlor tricks
      const rowId = row.getAttribute('data-row-id') || index;
      const rowContent = row.getAttribute('data-row-content') || '';
      
      console.log(`🔍 [DOM Scraping] Row ${index + 1} (ID: ${rowId}): ${rowText}`);
      
      return {
        id: rowId,
        content: rowContent,
        text: rowText,
        index: index
      };
    }).filter(row => row.text);
    
    // Capture pagination info for better agent context
    const paginationInfo = capturePaginationContext();
    
    const result = {
      headers,
      rows: rowsData.map(row => row.text).join('\n'), // Keep backward compatibility
      rowsWithIds: rowsData, // New structured data with IDs
      rowCount: visibleRows.length,
      totalRowsVisible: allRows.length,
      pagination: paginationInfo,
      capturedAt: new Date().toISOString()
    };
    
    console.log('🔍 [DOM Scraping] 🎯 Final captured data:', result);
    return result;
  }, []);

  /**
   * Captures active filters from the logic summary component
   * @returns {Object|null} Filter data with summary and metadata
   */
  const captureActiveFilters = useCallback(() => {
    console.log('🎯 [DOM Scraping] Looking for logic-summary-filters...');
    
    const logicSummary = document.getElementById('logic-summary-filters');
    if (!logicSummary) {
      console.log('🎯 [DOM Scraping] ❌ No logic summary found');
      return null;
    }
    
    console.log('🎯 [DOM Scraping] ✅ Found logic summary');
    
    // Look for "Active Filters" text to determine if filters are present
    const activeFiltersText = logicSummary.textContent || '';
    const hasActiveFilters = activeFiltersText.includes('Active Filters') && !activeFiltersText.includes('No Active Filters');
    
    if (!hasActiveFilters) {
      console.log('🎯 [DOM Scraping] No active filters detected');
      return {
        hasFilters: false,
        filterSummary: 'No active filters',
        capturedAt: new Date().toISOString()
      };
    }
    
    // Extract visible filter badges (elements with badge-like classes)
    const filterBadges = Array.from(logicSummary.querySelectorAll('[class*="bg-"][class*="text-"]:not(button)'))
      .map(el => el.textContent.trim())
      .filter(text => text && text.length > 0 && text !== 'Clear All' && text !== 'Active Filters')
      .slice(0, 10); // Limit to prevent overwhelm
    
    const result = {
      hasFilters: true,
      filterSummary: filterBadges.length > 0 ? filterBadges.join(', ') : 'Active filters detected',
      filterCount: filterBadges.length,
      capturedAt: new Date().toISOString()
    };
    
    console.log('🎯 [DOM Scraping] Captured filters:', result);
    return result;
  }, []);

  /**
   * Captures both table and filter data in a single call
   * @returns {Object} Combined capture data
   */
  const capturePageContext = useCallback(() => {
    const tableData = captureCurrentTableView();
    const filtersData = captureActiveFilters();
    
    if (tableData) {
      console.log(`🔍 [DOM Scraping] 🎉 Successfully captured ${tableData.rowCount} rows`);
      console.log('🔍 [DOM Scraping] Sample preview:', tableData.rows.split('\n').slice(0, 3));
    }
    
    if (filtersData) {
      console.log(`🎯 [DOM Scraping] 🎉 Captured filter state: ${filtersData.hasFilters ? 'Active' : 'None'}`);
      if (filtersData.hasFilters) {
        console.log('🎯 [DOM Scraping] Filter summary:', filtersData.filterSummary);
      }
    }
    
    return {
      tableData,
      filtersData
    };
  }, [captureCurrentTableView, captureActiveFilters]);

  return {
    captureCurrentTableView,
    captureActiveFilters,
    capturePageContext
  };
};
